import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { formatDateIST, formatTimeIST } from '../../lib/dateUtils';
import {
  Loader2, ArrowLeft, Phone, AlertTriangle, Pencil, Trash2,
  Stethoscope, FileText, Clock, ClipboardList, CheckCircle, Pill, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  checkin: { label: 'Check-in', color: 'bg-[#E6EFEA] text-[#0E5132] border-[rgba(14,81,50,0.15)]' },
  history: { label: 'History', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  doctor_consult: { label: 'Doctor Consult', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  prescription: { label: 'Prescription', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const edenStatusColors = {
  checked_in: 'bg-[#E6EFEA] text-[#0E5132]',
  checked_out: 'bg-slate-100 text-slate-600',
  scheduled: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-violet-100 text-violet-700',
};

export default function PatientProfilePage() {
  const { patientId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState({ local_visits: [], eden_visits: [] });
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [options, setOptions] = useState({ genders: ['male', 'female', 'other'] });

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({});

  const fetchPatient = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/${patientId}`, { withCredentials: true });
      setPatient(data);
    } catch (error) {
      toast.error('Failed to fetch patient');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [patientId, navigate]);

  const fetchVisits = useCallback(async () => {
    setVisitsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/${patientId}/visits`, { withCredentials: true });
      setVisits(data);
    } catch (error) {
      console.error('Failed to fetch visits', error);
    } finally {
      setVisitsLoading(false);
    }
  }, [patientId]);

  const fetchOptions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/options`, { withCredentials: true });
      setOptions(data);
    } catch (error) {
      console.error('Failed to fetch options', error);
    }
  }, []);

  useEffect(() => {
    fetchPatient();
    fetchVisits();
    fetchOptions();
  }, [fetchPatient, fetchVisits, fetchOptions]);

  const handleEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        allergies: typeof formData.allergies === 'string'
          ? formData.allergies.split(',').map(a => a.trim()).filter(Boolean)
          : formData.allergies,
      };
      await axios.put(`${API_URL}/api/patients/${patientId}`, payload, { withCredentials: true });
      toast.success('Patient updated');
      setIsEditOpen(false);
      fetchPatient();
    } catch (error) {
      toast.error('Failed to update patient');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = () => {
    setFormData({
      name: patient.name,
      age: patient.age,
      gender: patient.gender,
      phone: patient.phone,
      allergies: patient.allergies?.join(', ') || '',
    });
    setIsEditOpen(true);
  };

  const handleDeletePatient = async () => {
    if (!window.confirm(`Delete local profile for "${patient.name}"?\n\nThis removes the patient from this system only. Their profile in Eden remains untouched. Existing consult cards will be preserved as historical records.\n\nWhen Eden sends a new check-in, the profile will be recreated.`)) return;
    try {
      await axios.delete(`${API_URL}/api/patients/${patientId}`, { withCredentials: true });
      toast.success(`Patient profile "${patient.name}" deleted locally`);
      navigate(-1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete patient');
    }
  };

  const handleDeleteCard = async (cardId, patientName) => {
    if (!window.confirm(`Delete this consult card for "${patientName}"? This will remove the card, its sessions, and media permanently.`)) return;
    try {
      await axios.delete(`${API_URL}/api/consult-cards/${cardId}`, { withCredentials: true });
      toast.success('Consult card deleted');
      fetchVisits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete card');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="patient-profile-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Patient not found</p>
      </div>
    );
  }

  const totalVisits = visits.local_visits.length + visits.eden_visits.length;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="patient-profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground">
            {patient.name}
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {patient.age}yo {patient.gender} · {patient.phone}
          </p>
        </div>
        <Button variant="outline" onClick={openEditModal} className="rounded-full" data-testid="edit-patient-button">
          <Pencil className="w-4 h-4 mr-2" />
          Edit
        </Button>
        {user?.role === 'admin' && (
          <Button variant="outline" onClick={handleDeletePatient} className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="delete-patient-button">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Profile
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Info Card */}
        <Card className="border-border shadow-sm lg:col-span-1">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#E6EFEA] flex items-center justify-center">
                <span className="text-[#0E5132] font-heading font-semibold text-xl">
                  {patient.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <CardTitle className="font-heading text-lg">{patient.name}</CardTitle>
                <CardDescription className="flex items-center gap-1.5">
                  <Phone className="w-3 h-3" /> {patient.phone}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {patient.allergies?.length > 0 && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                  <span className="font-medium text-red-600 text-xs">Allergies</span>
                </div>
                <p className="text-sm text-red-700">{patient.allergies.join(', ')}</p>
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Age</span>
                <span className="font-medium">{patient.age} years</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gender</span>
                <span className="font-medium capitalize">{patient.gender}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Registered</span>
                <span className="text-xs">{formatDateIST(patient.created_at)}</span>
              </div>
            </div>

            <div className="pt-2 border-t space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Consult Cards</span>
                <Badge variant="outline" className="text-xs">{visits.local_visits.length}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Eden Visits</span>
                <Badge variant="outline" className="text-xs">{visits.eden_visits.length}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Visit History */}
        <Card className="border-border shadow-sm lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#0E5132]" />
              Visit History
              <Badge variant="outline" className="text-xs ml-1">{totalVisits} visits</Badge>
            </CardTitle>
            <CardDescription>Consultation cards and Eden appointment history</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[550px] pr-4">
              {visitsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#0E5132]" />
                </div>
              ) : totalVisits === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Clock className="w-10 h-10 text-slate-300 mb-3" />
                  <p className="text-sm text-muted-foreground">No visits recorded yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Local Consult Cards */}
                  {visits.local_visits.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Consultation Cards</h3>
                      {visits.local_visits.map((visit) => {
                        const stageConf = STAGE_CONFIG[visit.stage] || STAGE_CONFIG.checkin;
                        return (
                          <div
                            key={visit.id}
                            className="p-3 rounded-lg border border-slate-200 hover:border-[rgba(14,81,50,0.15)] hover:bg-[#E6EFEA]/20 transition-colors cursor-pointer"
                            onClick={() => navigate(`/consult-cards/${visit.id}`)}
                            data-testid={`visit-card-${visit.id}`}
                          >
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                                <Clock className="w-3 h-3 text-slate-400" />
                                <span className="text-xs font-medium text-slate-700">{formatDateIST(visit.created_at)}</span>
                                <span className="text-[10px] text-muted-foreground">{formatTimeIST(visit.created_at)}</span>
                                <Badge variant="outline" className={`text-[9px] ${stageConf.color}`}>{stageConf.label}</Badge>
                                {visit.eden_source && (
                                  <Badge className="h-4 px-1.5 text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 gap-0.5">
                                    <Zap className="w-2.5 h-2.5" /> Eden
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {visit.has_prescription && (
                                  <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200 gap-0.5">
                                    <Pill className="w-2.5 h-2.5" /> Rx
                                  </Badge>
                                )}
                                {user?.role === 'admin' && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteCard(visit.id, patient.name); }}
                                    className="p-1 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                    title="Delete this consult card"
                                    data-testid={`delete-card-${visit.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                            {visit.eden_service_name && (
                              <p className="text-xs text-emerald-600 font-medium mb-1">{visit.eden_service_name}</p>
                            )}
                            {visit.assigned_doctor_name && (
                              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                                <Stethoscope className="w-3 h-3" /> {visit.assigned_doctor_name}
                              </p>
                            )}
                            {visit.summaries?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {visit.summaries.slice(0, 2).map((s, i) => (
                                  <p key={i} className="text-[10px] text-slate-500 line-clamp-2 pl-2 border-l-2 border-slate-200">
                                    <span className="font-semibold uppercase text-slate-400 text-[8px]">{s.type === 'history' ? 'History' : 'Consult'}: </span>
                                    {s.summary}
                                  </p>
                                ))}
                              </div>
                            )}
                            {visit.notes && !visit.summaries?.length && (
                              <p className="text-[10px] text-muted-foreground mt-1">{visit.notes}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Eden Appointment History */}
                  {visits.eden_visits.length > 0 && (
                    <div className="space-y-2">
                      {visits.local_visits.length > 0 && (
                        <div className="flex items-center gap-2 pt-2">
                          <div className="flex-1 h-px bg-emerald-200" />
                          <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">Eden Appointment History</span>
                          <div className="flex-1 h-px bg-emerald-200" />
                        </div>
                      )}
                      <h3 className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                        {visits.local_visits.length === 0 ? 'Eden Appointment History' : ''}
                      </h3>
                      {visits.eden_visits.map((visit) => (
                        <div
                          key={visit.id}
                          className="p-3 rounded-lg border border-emerald-100 bg-emerald-50/30"
                          data-testid={`eden-visit-${visit.id}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3 text-emerald-500" />
                              <span className="text-xs font-medium text-slate-700">{visit.scheduled_date}</span>
                              {visit.scheduled_time && <span className="text-[10px] text-muted-foreground">{visit.scheduled_time}</span>}
                              <Badge variant="outline" className={`text-[9px] ${edenStatusColors[visit.status] || 'bg-slate-100 text-slate-500'}`}>
                                {visit.status?.replace('_', ' ')}
                              </Badge>
                              {visit.has_local_card && (
                                <Badge variant="outline" className="text-[9px] bg-[#E6EFEA] text-[#0E5132] border-[rgba(14,81,50,0.15)] gap-0.5">
                                  <FileText className="w-2.5 h-2.5" /> Card
                                </Badge>
                              )}
                            </div>
                            {visit.ref_number && (
                              <span className="text-[9px] text-muted-foreground font-mono">{visit.ref_number}</span>
                            )}
                          </div>
                          <p className="text-xs text-emerald-700 font-medium">{visit.service_name}</p>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                            {visit.service_type && <span>{visit.service_type === 'queue_based' ? 'Queue' : 'Appointment'}</span>}
                            {visit.token_number && <span>Token #{visit.token_number}</span>}
                            {visit.is_paid && <span className="text-emerald-600">Paid{visit.amount_paid > 0 ? ` (₹${visit.amount_paid})` : ''}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Edit Patient Modal — simplified */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-patient-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Patient</DialogTitle>
            <DialogDescription>Update patient information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="rounded-xl" data-testid="edit-patient-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Age</Label>
                <Input type="number" min="0" max="150" value={formData.age || ''} onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value, 10) })} required className="rounded-xl" data-testid="edit-patient-age" />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={formData.gender || 'female'} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="edit-patient-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options.genders || ['male', 'female', 'other']).map(g => (
                      <SelectItem key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="rounded-xl" data-testid="edit-patient-phone" />
            </div>
            <div className="space-y-2">
              <Label>Allergies (comma separated)</Label>
              <Input value={formData.allergies || ''} onChange={(e) => setFormData({ ...formData, allergies: e.target.value })} className="rounded-xl" data-testid="edit-patient-allergies" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-[#0E5132] hover:bg-[#063D2F] rounded-full" data-testid="edit-patient-submit">
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
